// 🔧 settings_voting.js
// ✅ Purpose: Admin-only Voting Settings page
// ✅ Features:
// - Standalone replacement for old Voting tab
// - Voting open toggle
// - Start/end time pickers
// - Leaderboard toggle + item limit
// - Best in Show enable toggle when supported by voting engine
// - Organizer-configurable maximum ballot score for judged voting
// - Loads/saves /events/{eventId}/settings/voting
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { getEventVotingType } from "../../utils/event_voting_type_helpers";
import { allowsBestInShow } from "../../utils/voting_engine_registry";
import { logger } from '../../utils/logger';


const SettingsVoting = () => {
  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const functions = getFunctions();
  const [votingOpen, setVotingOpen] = useState(false);
  const [votingStart, setVotingStart] = useState("");
  const [votingEnd, setVotingEnd] = useState("");
  const [leaderboardLimit, setLeaderboardLimit] = useState(5);
  const [resultsAudience, setResultsAudience] = useState("managers");
  const [bestInShowEnabled, setBestInShowEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWinnerResultsAt, setShowWinnerResultsAt] = useState("");
  const [winnerPlaceLimit, setWinnerPlaceLimit] = useState(3);
  const [showWinnerVoteCounts, setShowWinnerVoteCounts] = useState(true);

  // ✅ Flexible judged scoring defaults to the historical 100-point scale.
  // ✅ Existing root event values remain supported during compatibility transition.
  const [maxBallotPoints, setMaxBallotPoints] = useState(100);

  const currentVotingType = getEventVotingType(eventDoc);
  const canUseBestInShow = allowsBestInShow(currentVotingType);

  const isJudgedVoting =
    currentVotingType === "points" ||
    currentVotingType === "scorecard";
  const [publicEntryStart, setPublicEntryStart] = useState("");
  const [publicEntryEnd, setPublicEntryEnd] = useState("");

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  const formatLocalDateForInput = (date) => {
    const pad = (n) => n.toString().padStart(2, "0");
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const mi = pad(date.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      try {
        logger.log("🗳 Loading voting settings for event:", eventId);

        const snap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const data = snap.exists()
          ? snap.data()
          : {};

        setSettings(data);

        setVotingOpen(data.votingOpen === true);

        // ✅ Explicit results audience is authoritative when present.
        // Historical events retain their original showLeaderboard intent.
        const loadedResultsAudience =
          typeof data.resultsAudience === "string"
            ? data.resultsAudience.trim().toLowerCase()
            : (data.showLeaderboard === true ? "voters" : "managers");

        setResultsAudience(loadedResultsAudience);
        setLeaderboardLimit(data.leaderboardLimit || 5);
        setBestInShowEnabled(data.bestInShowEnabled === true);
        setShowWinnerResultsAt(data.showWinnerResultsAt || "");
        setWinnerPlaceLimit(data.winnerPlaceLimit || 3);
        setShowWinnerVoteCounts(data.showWinnerVoteCounts !== false);

        // ✅ New organizer setting is authoritative when present.
        // ✅ Legacy root event value remains the compatibility fallback.
        setMaxBallotPoints(
          Number(
            data.maxBallotPoints ??
            eventDoc?.maxBallotPoints ??
            100
          )
        );

        if (data.publicEntryStart?.toDate) {
          setPublicEntryStart(formatLocalDateForInput(data.publicEntryStart.toDate()));
        }

        if (data.publicEntryEnd?.toDate) {
          setPublicEntryEnd(formatLocalDateForInput(data.publicEntryEnd.toDate()));
        }

        if (data.votingStart?.toDate) {
          setVotingStart(formatLocalDateForInput(data.votingStart.toDate()));
        }

        if (data.votingEnd?.toDate) {
          setVotingEnd(formatLocalDateForInput(data.votingEnd.toDate()));
        }
      } catch (err) {
        console.error("❌ Failed to load voting settings:", err);

        setStatus({
          type: "error",
          message: "Failed to load voting settings.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [eventId, eventDoc]);

  const handleSaveVoting = async () => {
    if (!eventId) return;

    // ✅ Provide immediate organizer feedback before calling the trusted backend.
    if (
      isJudgedVoting &&
      (
        !Number.isInteger(Number(maxBallotPoints)) ||
        Number(maxBallotPoints) < 1 ||
        Number(maxBallotPoints) > 1000
      )
    ) {
      showStatus(setStatus, {
        type: "warning",
        message: "Maximum ballot points must be a whole number between 1 and 1000.",
      });

      return;
    }

    if (
      votingStart &&
      votingEnd &&
      new Date(votingEnd) < new Date(votingStart)
    ) {
      showStatus(setStatus, {
        type: "warning",
        message: "Voting end time must be after voting start time.",
      });

      return;
    }

    if (
      publicEntryStart &&
      publicEntryEnd &&
      new Date(publicEntryEnd) < new Date(publicEntryStart)
    ) {
      showStatus(setStatus, {
        type: "warning",
        message: "Public entry submission end time must be after the start time.",
      });

      return;
    }

    setSaving(true);

    try {

    const updateVotingSettings = httpsCallable(functions, "updateVotingSettings");

    await updateVotingSettings({
      eventId,
      votingOpen,
      votingStart: votingStart || null,
      votingEnd: votingEnd || null,

      // ✅ Flexible score scale is relevant only to judged voting.
      ...(isJudgedVoting
        ? { maxBallotPoints: Number(maxBallotPoints) }
        : {}),

      showLeaderboard: resultsAudience === "voters",
      resultsAudience,
      leaderboardLimit,
      bestInShowEnabled: canUseBestInShow ? bestInShowEnabled : false,
      showWinnerResultsAt,
      winnerPlaceLimit: Number(winnerPlaceLimit),
      showWinnerVoteCounts,
      publicEntryStart: publicEntryStart || null,
      publicEntryEnd: publicEntryEnd || null,
      reason: null,
    });

      setSettings((prev) => ({
        ...prev,
        votingOpen,
        showLeaderboard: resultsAudience === "voters",
        resultsAudience,
        leaderboardLimit,
        bestInShowEnabled: canUseBestInShow ? bestInShowEnabled : false,
        showWinnerResultsAt,
        winnerPlaceLimit: Number(winnerPlaceLimit),
        showWinnerVoteCounts,

        ...(isJudgedVoting
          ? { maxBallotPoints: Number(maxBallotPoints) }
          : {}),
      }));


      showStatus(setStatus, {
        type: "success",
        message: "Voting settings saved successfully.",
      });
    } catch (err) {
      console.error("❌ Failed to save voting settings:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save voting settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (
    !ready ||
    eventLoading ||
    settingsLoading ||
    isManager === null
  ) {
    return <FullPageSpinner message="Loading voting settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🗳 Voting Settings</h1>
            <p className="lead text-muted mb-0">
              Configure voting windows, leaderboard visibility, public entries, and winner display.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSaveVoting}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Settings"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Voting Engine</strong>
          </div>

          <div className="card-body">
            <div className="alert alert-info mb-0">
              <strong>Voting Type:</strong>{" "}
              {currentVotingType === "standard"
                ? "People's Choice Voting"
                : currentVotingType === "ranked_choice"
                ? "Ranked Choice Voting"
                : currentVotingType === "points"
                ? "Judged Points Voting"
                : currentVotingType === "scorecard"
                ? "Detailed Scorecard Voting (Legacy)"
                : currentVotingType}

              <div className="mt-2">
                {currentVotingType === "standard" &&
                  "Each voter selects a single item in each category. Winners are determined by total vote count."}

                {currentVotingType === "ranked_choice" &&
                  "Voters rank items in order of preference. Rankings are used to determine category winners through an elimination process."}

                {currentVotingType === "points" &&
                  "Judges score entries using configured scoring factors. Winners are determined by the highest average score."}

                {currentVotingType === "scorecard" &&
                  "Legacy Detailed Scorecard configuration. New events should use Judged Points Voting with Detailed Scorecard Mode."}
              </div>

              <div className="small mt-2">
                Voting type is managed by Let's Go Vote admins. If this voting engine is incorrect, contact Let's Go Vote before setup continues.
              </div>
            </div>

            {isJudgedVoting && (
              <div className="mt-4">
                <label
                  htmlFor="maxBallotPoints"
                  className="form-label fw-bold"
                >
                  Maximum Ballot Score
                </label>

                <input
                  type="number"
                  id="maxBallotPoints"
                  className="form-control"
                  min="1"
                  max="1000"
                  step="1"
                  value={maxBallotPoints}
                  onChange={(e) =>
                    setMaxBallotPoints(Number(e.target.value))
                  }
                />

                <div className="form-text">
                  Active scoring factors must add up to this maximum.
                  For example, five criteria worth 5 points each use
                  a maximum ballot score of 25.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-white">
                <strong>📝 Public Entry Submissions</strong>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="publicEntryStart" className="form-label">
                    Entry Submission Start
                  </label>

                  <input
                    type="datetime-local"
                    id="publicEntryStart"
                    className="form-control"
                    value={publicEntryStart}
                    onChange={(e) => setPublicEntryStart(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="publicEntryEnd" className="form-label">
                    Entry Submission End
                  </label>

                  <input
                    type="datetime-local"
                    id="publicEntryEnd"
                    className="form-control"
                    value={publicEntryEnd}
                    onChange={(e) => setPublicEntryEnd(e.target.value)}
                  />

                  <div className="form-text">
                    Leave either date blank to disable public entry submissions.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-white">
                <strong>🗳 Voting Period</strong>
              </div>

              <div className="card-body">
                <div className="form-check form-switch mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="votingOpenToggle"
                    checked={votingOpen}
                    onChange={(e) => setVotingOpen(e.target.checked)}
                  />

                  <label className="form-check-label" htmlFor="votingOpenToggle">
                    Voting is manually open
                  </label>
                </div>

                <div className="mb-3">
                  <label htmlFor="votingStart" className="form-label">
                    Voting Start
                  </label>

                  <input
                    type="datetime-local"
                    id="votingStart"
                    className="form-control"
                    value={votingStart}
                    onChange={(e) => setVotingStart(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="votingEnd" className="form-label">
                    Voting End
                  </label>

                  <input
                    type="datetime-local"
                    id="votingEnd"
                    className="form-control"
                    value={votingEnd}
                    onChange={(e) => setVotingEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card h-100 shadow-sm">
              <div className="card-header bg-white">
                <strong>📊 Leaderboard Settings</strong>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="resultsAudience" className="form-label fw-bold">
                    Who can view results?
                  </label>

                  <select
                    id="resultsAudience"
                    className="form-select"
                    value={resultsAudience}
                    onChange={(e) => {
                      const nextAudience = e.target.value;

                      setResultsAudience(nextAudience);

                    }}
                  >
                    <option value="voters">Voters / Signed-in Users</option>
                    <option value="judges">Judges / Jury</option>
                    <option value="managers">Managers</option>
                    <option value="admin">Admin Only</option>
                  </select>

                  <div className="form-text">
                    Controls who may view event results and leaderboard data.
                    Managers and Admins retain oversight when Judges / Jury is selected.
                  </div>
                </div>

                <label htmlFor="leaderboardLimitInput" className="form-label">
                  Limit top items per category
                </label>

                <input
                  type="number"
                  id="leaderboardLimitInput"
                  className="form-control"
                  min="1"
                  max="50"
                  value={leaderboardLimit}
                  onChange={(e) => setLeaderboardLimit(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          {canUseBestInShow && (
            <div className="col-12 col-lg-6">
              <div className="card h-100 shadow-sm">
                <div className="card-header bg-white">
                  <strong>🏆 Best in Show</strong>
                </div>

                <div className="card-body">
                  <div className="form-check form-switch mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="toggleBestInShow"
                      checked={bestInShowEnabled}
                      onChange={(e) => setBestInShowEnabled(e.target.checked)}
                    />

                    <label className="form-check-label" htmlFor="toggleBestInShow">
                      Enable “Best in Show” Category
                    </label>
                  </div>

                  <div className="small text-muted">
                    Best in Show is available for this voting engine.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🏆 Winner Results</strong>
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12 col-lg-4">
                <label className="form-label fw-bold">
                  Show Winner Results Date/Time
                </label>

                <input
                  type="datetime-local"
                  className="form-control"
                  value={showWinnerResultsAt}
                  onChange={(e) => setShowWinnerResultsAt(e.target.value)}
                />

                <div className="form-text">
                  Leave blank to show immediately.
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <label className="form-label fw-bold">
                  Places to Display
                </label>

                <select
                  className="form-select"
                  value={winnerPlaceLimit}
                  onChange={(e) =>
                    setWinnerPlaceLimit(Number(e.target.value))
                  }
                >
                  <option value={1}>1st Place Only</option>
                  <option value={2}>1st - 2nd Place</option>
                  <option value={3}>1st - 3rd Place</option>
                  <option value={4}>1st - 4th Place</option>
                  <option value={5}>1st - 5th Place</option>
                </select>

                <div className="form-text">
                  Controls how many winners are shown for each category.
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <label className="form-label fw-bold">
                  Vote Count Display
                </label>

                <div className="form-check form-switch mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="showWinnerVoteCounts"
                    checked={showWinnerVoteCounts}
                    onChange={(e) => setShowWinnerVoteCounts(e.target.checked)}
                  />

                  <label className="form-check-label" htmlFor="showWinnerVoteCounts">
                    Show vote count / score
                  </label>
                </div>

                <div className="form-text">
                  Turn off to show placements without totals.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Voting Settings</strong>

              {settings?.lastSaved?.toDate?.() && (
                <div className="text-muted small">
                  Last saved: {settings.lastSaved.toDate().toLocaleString()}
                </div>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSaveVoting}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Voting Settings"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

};

export default SettingsVoting;